import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import { motion } from "framer-motion";
import { Search, Filter, ShieldAlert, ArchiveX, ShieldCheck, MoreVertical, User, ChevronLeft, ChevronRight, Mail, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

export default function UserList() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("ALL"); // ALL, SEEKER, PROVIDER
    const [sortBy, setSortBy] = useState("latest"); // latest, risk
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, pages: 1 });
    const [actionLoading, setActionLoading] = useState(null); // ID of user being acted upon

    // Debounce Search
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Fetch Data
    useEffect(() => {
        async function fetchUsers() {
            setLoading(true);
            try {
                const query = new URLSearchParams({
                    q: debouncedSearch,
                    role: roleFilter,
                    sort: sortBy,
                    page,
                    limit: 10
                });

                // Using doFetch which handles token attachment
                // Note: Ensure /api/admin is correct in your backend route structure
                // Adjust path if your admin routes are mounted differently
                console.log(`📡 [FRONTEND] Fetching users... /admin/users?${query.toString()}`);
                const res = await api.get(`/admin/users?${query.toString()}`);
                setUsers(res.data.users);
                setPagination(res.data.pagination);
            } catch (err) {
                console.error("Failed to load users", err);
            } finally {
                setLoading(false);
            }
        }
        fetchUsers();
    }, [debouncedSearch, roleFilter, sortBy, page]);

    const [showBanModal, setShowBanModal] = useState(false);
    const [targetUser, setTargetUser] = useState(null);
    const [twoFactorCode, setTwoFactorCode] = useState("");

    // Actions
    const handleAction = async (userId, action, reason, authenticatorCode = null) => {
        if (action === "BAN" && !authenticatorCode) {
            const user = users.find(u => u.id === userId);
            setTargetUser(user);
            setShowBanModal(true);
            return;
        }

        if (action !== "BAN" && !confirm(`Are you sure you want to ${action} this user?`)) return;

        setActionLoading(userId);
        try {
            await api.post(`/admin/users/${userId}/action`, { 
                action, 
                reason,
                authenticatorCode 
            });
            
            // Refresh list (Filter out the banned user immediately)
            const updatedUsers = users.filter(u => !(u.id === userId && action === "BAN"));
            setUsers(updatedUsers);
            
            if (action === "BAN") {
                setShowBanModal(false);
                setTwoFactorCode("");
            }
            alert(`${action} successful.`);
        } catch (err) {
            alert(err.response?.data?.error || "Action failed");
        } finally {
            setActionLoading(null);
        }
    };

    const handleHardDelete = async (userId, code) => {
        setActionLoading(userId);
        try {
            await api.post(`/admin/users/${userId}/erase`, { authenticatorCode: code });
            setUsers(users.filter(u => u.id !== userId));
            alert("Sacred Erasure successful. The soul has been removed from the archives.");
        } catch (err) {
            alert(err.response?.data?.error || "Erasure failed");
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header / Controls */}
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-brand-blue outline-none transition-all"
                    />
                </div>

                <div className="flex gap-2">
                    {/* Role Toggles */}
                    <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
                        {["ALL", "SEEKER", "PROVIDER"].map((r) => (
                            <button
                                key={r}
                                onClick={() => { setRoleFilter(r); setPage(1); }}
                                className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${roleFilter === r
                                    ? "bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-white"
                                    : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                    }`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>

                    {/* Risk Filter */}
                    <button
                        onClick={() => setSortBy(sortBy === "risk" ? "latest" : "risk")}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 transition-colors ${sortBy === "risk"
                            ? "bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-900"
                            : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600"
                            }`}
                    >
                        <ShieldAlert className="w-4 h-4" />
                        High Risk
                    </button>
                </div>
            </div>

            {/* The Books (Table) */}
            <Card className="bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border-gray-200 dark:border-gray-800">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800 text-gray-500 dark:text-gray-400">
                                <tr>
                                    <th className="px-6 py-4 font-medium">User</th>
                                    <th className="px-6 py-4 font-medium">Role</th>
                                    <th className="px-6 py-4 font-medium">Safety Status</th>
                                    <th className="px-6 py-4 font-medium">Joined</th>
                                    <th className="px-6 py-4 font-medium text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {loading ? (
                                    <tr><td colSpan="5" className="p-8 text-center text-gray-400">Loading the archives...</td></tr>
                                ) : users.length === 0 ? (
                                    <tr><td colSpan="5" className="p-8 text-center text-gray-400">No souls found matching your query.</td></tr>
                                ) : (
                                    users.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.reportCount > 0 ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"
                                                        }`}>
                                                        {user.name?.[0]?.toUpperCase() || <User className="w-5 h-5" />}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-gray-900 dark:text-gray-100">{user.name}</div>
                                                        <div className="text-xs text-gray-500">{user.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${user.role === "ADMIN" ? "bg-purple-100 text-purple-700 border border-purple-200" :
                                                    user.role === "PROVIDER" ? "bg-blue-100 text-blue-700 border border-blue-200" :
                                                        "bg-gray-100 text-gray-700 border border-gray-200"
                                                    }`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {user.reportCount > 0 ? (
                                                    <div className="flex items-center gap-2 text-red-600 bg-red-50 dark:bg-red-900/10 px-3 py-1 rounded-full w-fit">
                                                        <ShieldAlert className="w-4 h-4" />
                                                        <span className="font-medium">{user.reportCount} Flags</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2 text-green-600">
                                                        <ShieldCheck className="w-4 h-4" />
                                                        <span className="text-xs">Good Standing</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-gray-500">
                                                {new Date(user.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">

                                                    {/* Email Button (Mailto for now) */}
                                                    <a href={`mailto:${user.email}`} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 transition-colors">
                                                        <Mail className="w-4 h-4" />
                                                    </a>

                                                    {/* The Hammer */}
                                                    <button
                                                        onClick={() => handleAction(user.id, "BAN", "Manual ban via Admin Console")}
                                                        className={`p-2 rounded-lg transition-colors border border-transparent ${!!user.deletedAt ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 hover:border-red-200"}`}
                                                        title={!!user.deletedAt ? "User already banned" : "Ban User"}
                                                        disabled={actionLoading === user.id || user.role === "ADMIN" || !!user.deletedAt}
                                                    >
                                                        {!!user.deletedAt ? <ShieldCheck className="w-4 h-4" /> : <ArchiveX className="w-4 h-4" />}
                                                    </button>
                                                    
                                                    {/* The Eraser (Hard Delete) */}
                                                    <button
                                                        onClick={() => {
                                                            const code = prompt("🚨 DANGER: SACRED ERASURE\n\nThis will permanently DELETE the user and all their data. This cannot be undone.\n\nEnter your 6-digit Admin 2FA code to confirm:");
                                                            if (code) {
                                                                handleHardDelete(user.id, code);
                                                            }
                                                        }}
                                                        className="p-2 hover:bg-red-600 hover:text-white rounded-lg text-red-400 transition-all"
                                                        title="Sacred Erasure (Hard Delete)"
                                                        disabled={actionLoading === user.id || user.role === "ADMIN"}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>

                                                    {/* More Actions Placeholder */}
                                                    <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 transition-colors">
                                                        <MoreVertical className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="flex justify-center items-center gap-4 pt-4">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="text-sm text-gray-500">
                        Page {pagination.current} of {pagination.pages}
                    </span>
                    <button
                        onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                        disabled={page === pagination.pages}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* 🔨 THE BAN HAMMER DIALOGUE */}
            {showBanModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6"
                    >
                        <div className="flex items-center gap-3 text-red-600 mb-4">
                            <ArchiveX className="w-6 h-6" />
                            <h3 className="text-xl font-bold">Initiate Permanent Ban?</h3>
                        </div>
                        
                        <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm leading-relaxed">
                            You are about to ban <span className="font-bold text-gray-900 dark:text-white">{targetUser?.name}</span>. 
                            This action is <span className="text-red-500 font-bold underline">permanent</span>. 
                            Their access will be revoked forever, and their network IP will be blacklisted from the sanctuary.
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Admin 2FA Confirmation</label>
                                <input 
                                    type="text" 
                                    placeholder="Enter 6-digit code"
                                    value={twoFactorCode}
                                    onChange={(e) => setTwoFactorCode(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-center text-2xl font-mono tracking-widest focus:ring-2 focus:ring-red-500 outline-none transition-all"
                                    maxLength={6}
                                />
                            </div>

                            <div className="flex gap-3">
                                <button 
                                    onClick={() => { setShowBanModal(false); setTwoFactorCode(""); }}
                                    className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={() => handleAction(targetUser.id, "BAN", "Manual ban via Admin Console", twoFactorCode)}
                                    disabled={twoFactorCode.length < 6 || actionLoading}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 disabled:opacity-50 transition-all shadow-lg shadow-red-500/20"
                                >
                                    {actionLoading ? "Swinging Hammer..." : "Confirm Ban"}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
